import {useRef} from 'react'

import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {AnimationProvider, Box, Grid, ThemeProvider} from '@primer/react-brand'
import {ArticleSeoSchema} from '@github-ui/swp-core/components/structuredData/ArticleSeoSchema'
import {BreadcrumbSeoSchema} from '@github-ui/swp-core/components/structuredData/BreadcrumbSeoSchema'
import {EventSeoSchema} from '@github-ui/swp-core/components/structuredData/EventSeoSchema'
import {ContentfulFaqGroup} from '@github-ui/swp-core/components/contentful/ContentfulFaqGroup'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import resolveResponse from 'contentful-resolve-response'

import {toResourceDetailsPayload} from '../../lib/types/payload'
import {toEntryCollection, toResourceDetailsPage, type Person} from '../../lib/types/contentful'

import {useCurrentVisibleHeading} from '../../lib/hooks/use-current-visible-heading'

import {TableOfContents} from '../../components/TableOfContents/TableOfContents'

import styles from './ResourceDetailsPage.module.css'

import Header from './Header'
import Content from './Content'
import Aside from './Aside'
import RelatedResources from '../../components/RelatedResources/RelatedResources'
import {appendFeatureFlagsToUrl} from '../../lib/utils'

export function ResourceDetailsPage() {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const payload = toResourceDetailsPayload(useRoutePayload<unknown>())
  const page = toResourceDetailsPage(toEntryCollection(resolveResponse(payload.contentfulRawJsonResponse)).at(0))
  const {template, settings} = page.fields
  const colorMode = settings?.fields.colorMode ?? 'light'

  const {body, featuredCta, featuredImage, form, hero, relatedResources, tableOfContents, people, faq} = template.fields

  const locationUrl = new URL(page.fields.path, ssrSafeLocation.origin)
  const queryParams = new URLSearchParams(ssrSafeLocation.search)
  const featureFlags = queryParams.get('_features') || ''

  const currentVisibleHeading = useCurrentVisibleHeading({ref: contentRef})
  const breadcrumbListItems =
    payload.breadcrumbLinks?.map(link => ({
      name: link.name,
      url: appendFeatureFlagsToUrl(link.href, featureFlags),
    })) || []

  const fallbackIndexPath = (() => {
    const segments = page.fields.path.split('/')
    segments.pop()
    return segments.join('/') || '/'
  })()
  const indexPath = payload.indexPath || fallbackIndexPath

  let jsonLdType = 'Thing'
  if (page.fields.path.includes('resources/events')) {
    jsonLdType = 'Event'
  } else if (page.fields.path.includes('resources/articles')) {
    jsonLdType = 'Article'
  }

  const contributors = people ? people.map((p: Person) => ({name: p.fields.fullName})) : []

  return (
    <ThemeProvider
      colorMode={colorMode}
      dir="ltr"
      style={{
        backgroundColor: 'var(--brand-color-canvas-default)',
      }}
    >
      <AnimationProvider runOnce visibilityOptions={0.2}>
        <Box marginBlockEnd={64}>
          <Grid className={styles.grid}>
            <Grid.Column span={12}>
              <Header component={template} breadcrumbs={breadcrumbListItems} contentFormCta={form?.fields.submitText} />
            </Grid.Column>

            <Grid.Column span={12}>
              <Grid>
                <Grid.Column span={{xsmall: 12, large: 7}}>
                  <div ref={contentRef}>
                    <Content component={template} socialShareUrl={locationUrl.toString()} indexPath={indexPath} />
                  </div>
                </Grid.Column>

                <Grid.Column className={styles.formWrapper} span={{xsmall: 12, large: 4}} start={{large: 9}}>
                  {tableOfContents && body ? (
                    <TableOfContents
                      analyticsId={page.sys.id}
                      content={body?.fields.text}
                      active={currentVisibleHeading}
                      featuredCta={featuredCta}
                    />
                  ) : (
                    <Aside component={template} locationUrl={locationUrl} />
                  )}
                </Grid.Column>
              </Grid>
            </Grid.Column>
          </Grid>
        </Box>

        {relatedResources && relatedResources.length > 0 ? (
          <Box
            paddingBlockStart={{narrow: 48, regular: 80}}
            paddingBlockEnd={{narrow: 64, regular: 96}}
            backgroundColor="subtle"
          >
            <Grid>
              <Grid.Column>
                <RelatedResources relatedResources={relatedResources} />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {faq ? (
          <Box style={{paddingTop: 'var(--base-size-96)', paddingBottom: 'var(--base-size-96)'}}>
            <ContentfulFaqGroup component={faq} headingLevel={'h2'} />
          </Box>
        ) : null}
      </AnimationProvider>
      {jsonLdType === 'Article' ? (
        <ArticleSeoSchema
          title={page.fields.title ?? hero?.fields.heading ?? ''}
          imageUrl={featuredImage?.fields.file?.url}
          contributors={contributors}
          dateModified={page.sys.updatedAt}
          datePublished={page.sys.createdAt}
        />
      ) : jsonLdType === 'Event' ? (
        <EventSeoSchema
          title={page.fields.title ?? hero?.fields.heading ?? ''}
          description={hero?.fields.description}
          imageUrl={featuredImage?.fields.file?.url}
          contributors={contributors}
        />
      ) : (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Thing',
            name: hero?.fields.heading,
            description: hero?.fields.description,
          })}
        </script>
      )}
      <BreadcrumbSeoSchema items={breadcrumbListItems} />
    </ThemeProvider>
  )
}

try{ ResourceDetailsPage.displayName ||= 'ResourceDetailsPage' } catch {}